import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton
} from '@mui/material';
import EditQuestionModal from './EditQuestionModal';
import EditSectionModal from './EditSectionModal';
import SingleQuestionPanel from './SingleQuestionPanel';

// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import { ApplicationDTO, ApplicationMetricsDTO } from "../../../../types/Applications";
import {
    QuestionDTO,
    QuestionCreateDTO,
    QuestionType,
    ApplicantRoleType,
} from '../../../../types/ApplicationQuestion';
import ApplicationService from '../../../../services/ApplicationService';
import ApplicationQuestionService from '../../../../services/ApplicationQuestionService';
import AIService from '../../../../services/AIService';
import { ApplicationQuestionsRequest } from '../../../../types/AI';

// Questions Component Props
interface QuestionsProps {
    campaignId: string;
    organizationId: string;
    onMetricsChange?: (metrics: ApplicationMetricsDTO | null) => void;
    isReadOnly?: boolean;
}

// Questions Panel Component
const QuestionsPanel: React.FC<QuestionsProps> = ({ campaignId, organizationId, onMetricsChange, isReadOnly = false }) => {
    // Application details state
    const [applicationDetails, setApplicationDetails] = useState<ApplicationDTO | null>(null);
    const [questions, setQuestions] = useState<QuestionDTO[]>([]);

    // Loading and error states

    const [loading, setLoading] = useState<boolean>(true);
    // Only setter needed to clear error; state value unused to avoid lint
    const [, setError] = useState<string | null>(null);


    // Edit question modal state
    const [showSectionModal, setShowSectionModal] = useState<boolean>(false);
    const [currentSection, setCurrentSection] = useState<QuestionDTO | null>(null);
    const [showEditModal, setShowEditModal] = useState<boolean>(false);
    const [currentQuestion, setCurrentQuestion] = useState<QuestionDTO | null>(null);

    // Question edit handler
    const handleEditQuestion = (questionId: string) => {
        if (isReadOnly) return;
        const questionToEdit = questions.find(q => q.id === questionId);
        if (questionToEdit) {
            setCurrentQuestion(questionToEdit);
            setShowEditModal(true);
        }
    };

    const handleAddSection = () => {
        if (isReadOnly) return;
        if (!applicationDetails) return;
        const newSection: QuestionDTO = {
            id: `temp-${generateUUID()}`, // Temporary client-side ID
            application_id: applicationDetails.id,
            question_type: QuestionType.SECTION,
            question_text: 'New Section',
            required: false,
            for_role: ApplicantRoleType.BOTH,
            options: undefined,


        };

        // Add to local state only; will persist after modal save
        const newList = [...questions, newSection];
        setQuestions(newList);

        // Open section modal for editing newly added section
        setCurrentSection(newSection);
        setShowSectionModal(true);
    };

    // Handle section save from modal
    const handleSectionSave = async (updatedSection: QuestionDTO) => {
        try {
            let savedSection: QuestionDTO;

            if (updatedSection.id.startsWith('temp-')) {
                // Create new section using addQuestion
                const createData: QuestionCreateDTO = {
                    question_type: updatedSection.question_type,
                    question_text: updatedSection.question_text,
                    required: updatedSection.required,
                    for_role: updatedSection.for_role,
                    options: updatedSection.options,
                };
                const questionService = new ApplicationQuestionService();
                const result = await questionService.addQuestion(
                    organizationId,
                    campaignId,
                    createData
                );
                // Cast Question to QuestionDTO, handling null matching_question_id
                savedSection = {
                    ...result,
                    matching_question_id: result.matching_question_id || undefined
                } as QuestionDTO;
            } else {
                // Update existing section
                const updateData = {
                    question_text: updatedSection.question_text,
                    required: updatedSection.required,
                    for_role: updatedSection.for_role,
                    options: updatedSection.options,
                };
                const questionService = new ApplicationQuestionService();
                const result = await questionService.updateQuestion(
                    organizationId,
                    campaignId,
                    updatedSection.id,
                    updateData
                );
                // Cast Question to QuestionDTO, handling null matching_question_id
                savedSection = {
                    ...result,
                    matching_question_id: result.matching_question_id || undefined
                } as QuestionDTO;
            }

            // Update local state
            setQuestions(prevQuestions =>
                prevQuestions.map(q => q.id === updatedSection.id ? savedSection : q)
            );

            // Close modal
            setShowSectionModal(false);
            setCurrentSection(null);

            // Metrics will be updated by the parent application panel
        } catch (error) {
            console.error('Failed to save section:', error);
        }
    };

    // Handle section modal close
    const handleSectionModalClose = () => {
        setShowSectionModal(false);
        setCurrentSection(null);
    };

    const handleGenerateAIQuestions = async () => {
        if (isReadOnly) return;
        if (!applicationDetails) return;

        try {
            const aiService = new AIService();
            const payload: ApplicationQuestionsRequest = {
                questions: [],
                organization_details: { name: 'Mentoring Fusion' },
                campaign_details: { name: 'Campaign' },
                user_input: null
            } as any;
            const aiQuestions = await aiService.generateApplicationQuestions(payload);

            const newQs: QuestionDTO[] = aiQuestions.map((q: any) => ({
                id: `temp-${generateUUID()}`,
                application_id: applicationDetails.id,
                question_type: q.question_type,
                question_text: q.question_text,
                required: q.required ?? false,
                for_role: q.for_role,
                options: q.options?.options ?? [],
                matching_importance: q.matching_importance,

                updated_at: new Date().toISOString()
            }));

            setQuestions(prev => [...prev, ...newQs]);

            // Save AI-generated questions to API
            saveAIGeneratedQuestions(newQs);

            // persist order locally only; actual save occurs on individual edits
        } catch (e) {
            console.error('Failed to generate AI questions', e);
        } finally {

        }
    };

    const handleAddQuestion = () => {
        if (isReadOnly) return;
        if (!applicationDetails) return;
        const newQuestion: QuestionDTO = {
            id: `temp-${generateUUID()}`, // Temporary client-side ID
            application_id: applicationDetails.id,
            question_type: QuestionType.SHORT_ANSWER,
            question_text: 'New Question',
            required: false,
            for_role: ApplicantRoleType.BOTH,
            options: undefined,


        };

        // Add to local state only; will persist after modal save
        const newList = [...questions, newQuestion];
        setQuestions(newList);

        // Open modal for editing newly added question
        setCurrentQuestion(newQuestion);
        setShowEditModal(true);
    };

    const handleQuestionSave = async (savedQuestion: QuestionDTO) => {
        if (isReadOnly) return;
        try {
            const service = new ApplicationService();
            // Ensure options is a dictionary, converting from array if necessary
            const optionsAsDict = Array.isArray(savedQuestion.options) ? savedQuestion.options.reduce((dict, option, index) => {
                dict[`option_${index}`] = option;
                return dict;
            }, {} as Record<string, string>) : (savedQuestion.options || {});

            const questionToSave = {
                ...savedQuestion,
                options: optionsAsDict
            };

            // Local version keeps options as is for UI consistency
            const localQuestion: QuestionDTO = {
                ...savedQuestion
            };
            let currentList: QuestionDTO[];
            if (savedQuestion.id.startsWith('temp-')) {
                // New question - create via API
                const created = await service.addQuestion(organizationId, campaignId, questionToSave);
                currentList = questions.map(q => q.id === savedQuestion.id ? created : q);
                setQuestions(currentList);
            } else {
                // Existing question - update via API
                await service.updateQuestion(organizationId, campaignId, savedQuestion.id, questionToSave);
                currentList = questions.map(q => q.id === savedQuestion.id ? localQuestion : q);
                setQuestions(currentList);
            }

            // ensure order still correct
            await service.updateQuestionOrder(organizationId, campaignId, { question_ids: currentList.map(q => q.id) });
            const met = await service.getApplicationMetrics(organizationId, campaignId);

            if (onMetricsChange) onMetricsChange(met);
        } catch (e) {
            console.error('Failed to save question', savedQuestion, e);
        }

        setShowEditModal(false);
        setCurrentQuestion(null);
    };

    // Question delete handler
    const handleDeleteQuestion = async (questionId: string) => {
        if (isReadOnly) return;
        try {
            const service = new ApplicationService();
            if (applicationDetails) {
                await service.deleteQuestion(organizationId, campaignId, questionId);
                const remaining = questions.filter(q => q.id !== questionId);
                setQuestions(remaining);
                await service.updateQuestionOrder(organizationId, campaignId, { question_ids: remaining.map(q => q.id) });
                const met = await service.getApplicationMetrics(organizationId, campaignId);

                if (onMetricsChange) onMetricsChange(met);
            }
        } catch (e) {
            console.error('Failed to delete question', e);
        }
    };

    // Question reordering handlers
    const handleMoveQuestionUp = async (question: QuestionDTO) => {
        if (isReadOnly || !applicationDetails) return;

        const pairs = getPairedQuestions();
        const currentPairIndex = pairs.findIndex(pair =>
            pair.bothQuestion?.id === question.id ||
            pair.mentorQuestion?.id === question.id ||
            pair.menteeQuestion?.id === question.id
        );

        if (currentPairIndex <= 0) return; // Already at top or not found

        // Create new pairs array with swapped positions
        const newPairs = [...pairs];
        [newPairs[currentPairIndex - 1], newPairs[currentPairIndex]] = [newPairs[currentPairIndex], newPairs[currentPairIndex - 1]];

        // Rebuild questions array from the reordered pairs
        const newQuestions: QuestionDTO[] = [];
        newPairs.forEach(pair => {
            if (pair.bothQuestion) {
                newQuestions.push(pair.bothQuestion);
            }
            if (pair.mentorQuestion) {
                newQuestions.push(pair.mentorQuestion);
            }
            if (pair.menteeQuestion) {
                newQuestions.push(pair.menteeQuestion);
            }
        });

        // Update local state immediately for responsive UI
        setQuestions(newQuestions);

        // Persist order to API
        try {
            const questionService = new ApplicationQuestionService();
            await questionService.updateQuestionOrder(
                organizationId,
                campaignId,
                { question_ids: newQuestions.map(q => q.id) }
            );
        } catch (error) {
            console.error('Failed to update question order:', error);
            // Revert on error
            setQuestions(questions);
        }
    };

    const handleMoveQuestionDown = async (question: QuestionDTO) => {
        if (isReadOnly || !applicationDetails) return;

        const pairs = getPairedQuestions();
        const currentPairIndex = pairs.findIndex(pair =>
            pair.bothQuestion?.id === question.id ||
            pair.mentorQuestion?.id === question.id ||
            pair.menteeQuestion?.id === question.id
        );

        if (currentPairIndex >= pairs.length - 1 || currentPairIndex === -1) return; // Already at bottom or not found

        // Create new pairs array with swapped positions
        const newPairs = [...pairs];
        [newPairs[currentPairIndex], newPairs[currentPairIndex + 1]] = [newPairs[currentPairIndex + 1], newPairs[currentPairIndex]];

        // Rebuild questions array from the reordered pairs
        const newQuestions: QuestionDTO[] = [];
        newPairs.forEach(pair => {
            if (pair.bothQuestion) {
                newQuestions.push(pair.bothQuestion);
            }
            if (pair.mentorQuestion) {
                newQuestions.push(pair.mentorQuestion);
            }
            if (pair.menteeQuestion) {
                newQuestions.push(pair.menteeQuestion);
            }
        });

        // Update local state immediately for responsive UI
        setQuestions(newQuestions);

        // Persist order to API
        try {
            const questionService = new ApplicationQuestionService();
            await questionService.updateQuestionOrder(
                organizationId,
                campaignId,
                { question_ids: newQuestions.map(q => q.id) }
            );
        } catch (error) {
            console.error('Failed to update question order:', error);
            // Revert on error
            setQuestions(questions);
        }
    };

    // Save AI-generated questions to API
    const saveAIGeneratedQuestions = async (questionsToSave: QuestionDTO[]) => {
        if (!applicationDetails) return;
        try {
            const applicationService = new ApplicationService();
            for (const question of questionsToSave) {
                // Ensure options is a dictionary, converting from array if necessary
                const optionsAsDict = Array.isArray(question.options) ? question.options.reduce((dict, option, index) => {
                    dict[`option_${index}`] = option;
                    return dict;
                }, {} as Record<string, string>) : (question.options || {});

                const questionToSave = {
                    ...question,
                    options: optionsAsDict
                };
                await applicationService.addQuestion(
                    applicationDetails.organization_id,
                    applicationDetails.campaign_id,
                    questionToSave
                );
            }
            console.log('AI-generated questions saved successfully');
            // Refresh the questions list after saving
            const updatedQuestions = await applicationService.listQuestions(
                applicationDetails.organization_id,
                applicationDetails.campaign_id
            );
            setQuestions(updatedQuestions);
            // Update metrics after saving AI questions
            const updatedMetrics = await applicationService.getApplicationMetrics(
                applicationDetails.organization_id,
                applicationDetails.campaign_id
            );
            if (onMetricsChange) onMetricsChange(updatedMetrics);
        } catch (error) {
            console.error('Error saving AI-generated questions:', error);
        }
    };

    // Generate a UUID v4 (GUID)
    const generateUUID = () => {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : ((r & 0x3) | 0x8);
            return v.toString(16);
        });
    };

    const fetchApplicationData = React.useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const applicationService = new ApplicationService();
            const applicationData = await applicationService.getApplication(organizationId, campaignId);
            setApplicationDetails(applicationData);
            const questionService = new ApplicationQuestionService();
            const questionsData = await questionService.listQuestions(organizationId, campaignId);
            setQuestions(questionsData);
            console.log('Application data fetched successfully:', applicationData);
        } catch (err) {
            console.error('Error fetching application data:', err);
            setError('Failed to load application details. Please try again later.');
        } finally {
            setLoading(false);
        }
    }, [campaignId, organizationId]);

    // Fetch application data when component mounts or campaignId changes
    useEffect(() => {
        console.log('Fetching application data for campaign:', campaignId);
        fetchApplicationData();
    }, [fetchApplicationData, campaignId]);

    const handleModalClose = () => {
        setShowEditModal(false);
        setCurrentQuestion(null);
    };

    const getPairedQuestions = () => {
        const pairs: { mentorQuestion: QuestionDTO | null, menteeQuestion: QuestionDTO | null, bothQuestion: QuestionDTO | null, pairId: string }[] = [];
        const processedIds = new Set<string>();

        questions.forEach(question => {
            if (processedIds.has(question.id)) return;

            if (question.for_role === ApplicantRoleType.BOTH) {
                pairs.push({ mentorQuestion: null, menteeQuestion: null, bothQuestion: question, pairId: question.id });
                processedIds.add(question.id);
            } else {
                const oppositeRole = question.for_role === ApplicantRoleType.MENTOR ? ApplicantRoleType.MENTEE : ApplicantRoleType.MENTOR;
                const relatedQuestion = questions.find(q => (q.matching_question_id === question.id || question.matching_question_id === q.id) && q.for_role === oppositeRole);

                if (question.for_role === ApplicantRoleType.MENTOR) {
                    pairs.push({ mentorQuestion: question, menteeQuestion: relatedQuestion || null, bothQuestion: null, pairId: question.id });
                } else {
                    pairs.push({ mentorQuestion: relatedQuestion || null, menteeQuestion: question, bothQuestion: null, pairId: question.id });
                }
                processedIds.add(question.id);
                if (relatedQuestion) processedIds.add(relatedQuestion.id);
            }
        });

        return pairs;
    };

    const addRelatedQuestion = (baseQuestion: QuestionDTO) => {
        if (isReadOnly) return;
        const oppositeRole = baseQuestion.for_role === ApplicantRoleType.MENTOR ? ApplicantRoleType.MENTEE : ApplicantRoleType.MENTOR;
        const newQuestion: QuestionDTO = {
            id: `temp-${generateUUID()}`,
            application_id: baseQuestion.application_id,
            question_text: `Related question for ${oppositeRole.toLowerCase()}`,
            question_type: baseQuestion.question_type,
            for_role: oppositeRole,
            required: baseQuestion.required,
            options: baseQuestion.options,
            matching_importance: baseQuestion.matching_importance,
            matching_question_id: baseQuestion.id,
        };
        setQuestions([...questions, newQuestion]);
        setCurrentQuestion(newQuestion);
        setShowEditModal(true);
    };

    if (loading || !applicationDetails) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading application questions...</Typography>
            </Box>
        );
    }

    return (
        <Box>

            {/* Add Question/Section Buttons */}
            {!isReadOnly && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        onClick={handleAddQuestion}
                        disabled={isReadOnly}
                    >
                        Add Question
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAddSection}
                        disabled={isReadOnly}
                    >
                        Add Section
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleGenerateAIQuestions}
                        disabled={isReadOnly}
                    >
                        Generate with AI
                    </Button>
                </Box>
            )}

            {/* Questions Table */}
            <Box>
                {questions.length === 0 ? (
                    <Typography>No questions available.</Typography>
                ) : (
                    <Table sx={{ bgcolor: 'background.paper', borderRadius: 2, overflow: 'hidden', boxShadow: 1, tableLayout: 'fixed', width: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '50%', borderRight: '1px solid rgba(224, 224, 224, 1)', padding: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Mentor Questions</Typography>
                                </TableCell>
                                <TableCell sx={{ width: '50%', padding: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Mentee Questions</Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {getPairedQuestions().map((pair, pairIndex) => (
                                <TableRow key={pair.pairId} sx={{ borderTop: '1px solid rgba(224, 224, 224, 1)' }}>
                                    {pair.bothQuestion ? (
                                        <TableCell colSpan={2} sx={{ verticalAlign: 'top' }}>
                                            {pair.bothQuestion.question_type === QuestionType.SECTION ? (
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    py: 2,
                                                    px: 1
                                                }}>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontWeight: 'bold',
                                                            color: 'text.primary',
                                                            textAlign: 'center',
                                                            flex: 1
                                                        }}
                                                    >
                                                        {pair.bothQuestion.question_text}
                                                    </Typography>
                                                    {!isReadOnly && (
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleMoveQuestionUp(pair.bothQuestion!)}
                                                                disabled={pairIndex === 0}
                                                                sx={{ color: pairIndex === 0 ? 'text.disabled' : 'text.secondary' }}
                                                            >
                                                                <ArrowUpwardIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleMoveQuestionDown(pair.bothQuestion!)}
                                                                disabled={pairIndex === getPairedQuestions().length - 1}
                                                                sx={{ color: pairIndex === getPairedQuestions().length - 1 ? 'text.disabled' : 'text.secondary' }}
                                                            >
                                                                <ArrowDownwardIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    setCurrentSection(pair.bothQuestion!);
                                                                    setShowSectionModal(true);
                                                                }}
                                                                sx={{ color: 'text.secondary' }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteQuestion(pair.bothQuestion!.id)}
                                                                sx={{ color: 'error.main' }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </Box>
                                            ) : (
                                                <SingleQuestionPanel
                                                    question={pair.bothQuestion}
                                                    onEdit={(q) => handleEditQuestion(q.id)}
                                                    onDelete={(q) => handleDeleteQuestion(q.id)}
                                                    onMoveUp={handleMoveQuestionUp}
                                                    onMoveDown={handleMoveQuestionDown}
                                                    canMoveUp={pairIndex > 0}
                                                    canMoveDown={pairIndex < getPairedQuestions().length - 1}
                                                    readOnly={isReadOnly}
                                                />
                                            )}
                                        </TableCell>
                                    ) : (
                                        <>
                                            <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)', verticalAlign: 'top', width: '50%', padding: 2 }}>
                                                {pair.mentorQuestion ? (
                                                    pair.mentorQuestion.question_type === QuestionType.SECTION ? (
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            py: 2,
                                                            px: 1
                                                        }}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    color: 'text.primary',
                                                                    textAlign: 'center',
                                                                    flex: 1
                                                                }}
                                                            >
                                                                {pair.mentorQuestion.question_text}
                                                            </Typography>
                                                            {!isReadOnly && (
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleMoveQuestionUp(pair.mentorQuestion!)}
                                                                        disabled={pairIndex === 0}
                                                                        sx={{ color: pairIndex === 0 ? 'text.disabled' : 'text.secondary' }}
                                                                    >
                                                                        <ArrowUpwardIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleMoveQuestionDown(pair.mentorQuestion!)}
                                                                        disabled={pairIndex === getPairedQuestions().length - 1}
                                                                        sx={{ color: pairIndex === getPairedQuestions().length - 1 ? 'text.disabled' : 'text.secondary' }}
                                                                    >
                                                                        <ArrowDownwardIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setCurrentSection(pair.mentorQuestion!);
                                                                            setShowSectionModal(true);
                                                                        }}
                                                                        sx={{ color: 'text.secondary' }}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteQuestion(pair.mentorQuestion!.id)}
                                                                        sx={{ color: 'error.main' }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <SingleQuestionPanel
                                                            question={pair.mentorQuestion}
                                                            onEdit={(q) => handleEditQuestion(q.id)}
                                                            onDelete={(q) => handleDeleteQuestion(q.id)}
                                                            onMoveUp={handleMoveQuestionUp}
                                                            onMoveDown={handleMoveQuestionDown}
                                                            canMoveUp={pairIndex > 0}
                                                            canMoveDown={pairIndex < getPairedQuestions().length - 1}
                                                            readOnly={isReadOnly}
                                                        />
                                                    )
                                                ) : !isReadOnly && pair.menteeQuestion ? (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => addRelatedQuestion(pair.menteeQuestion!)}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        Add Related Mentor Question
                                                    </Button>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">No question</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top', width: '50%', padding: 2 }}>
                                                {pair.menteeQuestion ? (
                                                    pair.menteeQuestion.question_type === QuestionType.SECTION ? (
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            py: 2,
                                                            px: 1
                                                        }}>
                                                            <Typography
                                                                variant="h6"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    color: 'text.primary',
                                                                    textAlign: 'center',
                                                                    flex: 1
                                                                }}
                                                            >
                                                                {pair.menteeQuestion.question_text}
                                                            </Typography>
                                                            {!isReadOnly && (
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleMoveQuestionUp(pair.menteeQuestion!)}
                                                                        disabled={pairIndex === 0}
                                                                        sx={{ color: pairIndex === 0 ? 'text.disabled' : 'text.secondary' }}
                                                                    >
                                                                        <ArrowUpwardIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleMoveQuestionDown(pair.menteeQuestion!)}
                                                                        disabled={pairIndex === getPairedQuestions().length - 1}
                                                                        sx={{ color: pairIndex === getPairedQuestions().length - 1 ? 'text.disabled' : 'text.secondary' }}
                                                                    >
                                                                        <ArrowDownwardIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setCurrentSection(pair.menteeQuestion!);
                                                                            setShowSectionModal(true);
                                                                        }}
                                                                        sx={{ color: 'text.secondary' }}
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteQuestion(pair.menteeQuestion!.id)}
                                                                        sx={{ color: 'error.main' }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    ) : (
                                                        <SingleQuestionPanel
                                                            question={pair.menteeQuestion}
                                                            onEdit={(q) => handleEditQuestion(q.id)}
                                                            onDelete={(q) => handleDeleteQuestion(q.id)}
                                                            onMoveUp={handleMoveQuestionUp}
                                                            onMoveDown={handleMoveQuestionDown}
                                                            canMoveUp={pairIndex > 0}
                                                            canMoveDown={pairIndex < getPairedQuestions().length - 1}
                                                            readOnly={isReadOnly}
                                                        />
                                                    )
                                                ) : !isReadOnly && pair.mentorQuestion ? (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => addRelatedQuestion(pair.mentorQuestion!)}
                                                        sx={{ mt: 1 }}
                                                    >
                                                        Add Related Mentee Question
                                                    </Button>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">No question</Typography>
                                                )}
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </Box>

            {/* Edit Question Modal */}
            {showEditModal && currentQuestion && (
                <EditQuestionModal
                    open={showEditModal}
                    question={currentQuestion}
                    questions={questions}
                    onClose={handleModalClose}
                    onSave={handleQuestionSave}
                />
            )}

            {/* Edit Section Modal */}
            {showSectionModal && currentSection && (
                <EditSectionModal
                    open={showSectionModal}
                    section={currentSection}
                    onClose={handleSectionModalClose}
                    onSave={handleSectionSave}
                />
            )}
        </Box>
    );
};

export default QuestionsPanel;